import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import {
  ShoppingBag,
  User,
  ExternalLink,
  LogOut,
  SlidersHorizontal,
  PackageCheck,
} from "lucide-react";
import AuthModal from "../auth/AuthModal";

export default function Header() {
  const { user, logout } = useAuth();
  const { cart, setIsOpen } = useCart();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  return (
    <>
      {/* Sticky Dark Glass Header */}
      <header className="sticky top-0 z-40 bg-[#0A0A0B]/90 backdrop-blur-md border-b border-white/10 transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
          
          {/* Logo & Brand */}
          <Link
            to="/"
            data-testid="header-brand-logo"
            className="flex items-center gap-3 group focus:outline-none"
          >
            <img
              src="/logo.png"
              alt="Joyce SXY"
              className="h-10 w-auto object-contain"
            />
            <span className="font-heading text-2xl font-bold tracking-normal text-[#FF8C69]">
              Joyce SXY
            </span>
          </Link>

          {/* Center Navigation */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <Link
              to="/"
              data-testid="nav-vitrine-link"
              className="text-white hover:text-[#FF8C69] transition-colors"
            >
              Catálogo
            </Link>
            <Link
              to="/pedidos"
              data-testid="nav-orders-link"
              className="text-[#A1A1AA] hover:text-white transition-colors"
            >
              Meus Pedidos
            </Link>
            <a
              href="https://joycesxy.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="main-site-nav-link"
              className="flex items-center gap-1.5 text-[#A1A1AA] hover:text-[#FF8C69] transition-colors"
            >
              <span>joycesxy.com</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            {user && (user.role === "admin" || user.role === "catalog_manager" || user.role === "support") && (
              <Link
                to="/admin"
                data-testid="nav-admin-link"
                className="text-[#FF8C69] hover:text-white transition-colors flex items-center gap-1.5 font-bold"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span>Painel</span>
              </Link>
            )}
          </nav>

          {/* Right Action Icons */}
          <div className="flex items-center gap-3 sm:gap-4">
            
            {/* User Account / SSO Login */}
            {user ? (
              <div className="relative">
                <button
                  data-testid="user-menu-btn"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#121214] border border-white/10 hover:border-[#FF8C69]/50 transition-all text-xs"
                >
                  <div className="w-7 h-7 rounded-lg bg-[#FF8C69] flex items-center justify-center text-[#0A0A0B] font-bold">
                    {user.name.charAt(0)}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-white font-medium leading-none truncate max-w-[100px]">
                      {user.name.split(" ")[0]}
                    </p>
                    <span className="text-[10px] text-[#FF8C69] font-bold uppercase">
                      {user.role === "admin" ? "Admin" : "Membro"}
                    </span>
                  </div>
                </button>

                {showUserDropdown && (
                  <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-[#121214] border border-white/10 shadow-2xl p-2 z-50 animate-in fade-in zoom-in-95">
                    <div className="px-3 py-2 border-b border-white/10">
                      <p className="text-xs font-semibold text-white">{user.name}</p>
                      <p className="text-[11px] text-[#A1A1AA] truncate">{user.email}</p>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/pedidos"
                        onClick={() => setShowUserDropdown(false)}
                        data-testid="dropdown-orders-link"
                        className="flex items-center gap-2 px-3 py-2 text-xs text-[#A1A1AA] hover:text-white hover:bg-white/5 rounded-xl transition-colors"
                      >
                        <PackageCheck className="w-4 h-4 text-[#FF8C69]" />
                        <span>Meus Pedidos</span>
                      </Link>
                      {(user.role === "admin" || user.role === "catalog_manager") && (
                        <Link
                          to="/admin"
                          onClick={() => setShowUserDropdown(false)}
                          data-testid="dropdown-admin-link"
                          className="flex items-center gap-2 px-3 py-2 text-xs text-[#FF8C69] hover:bg-white/5 rounded-xl transition-colors font-semibold"
                        >
                          <SlidersHorizontal className="w-4 h-4" />
                          <span>Painel Administrativo</span>
                        </Link>
                      )}
                    </div>
                    <div className="border-t border-white/10 pt-1">
                      <button
                        onClick={() => {
                          setShowUserDropdown(false);
                          logout();
                        }}
                        data-testid="logout-btn"
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sair da Conta</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                data-testid="login-modal-open-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-bold bg-[#121214] border border-white/10 text-[#FF8C69] hover:bg-[#FF8C69] hover:text-[#0A0A0B] transition-all"
              >
                <User className="w-4 h-4" />
                <span>Entrar</span>
              </button>
            )}

            {/* Shopping Bag / Cart Drawer Button */}
            <button
              onClick={() => setIsOpen(true)}
              data-testid="header-cart-btn"
              className="relative p-2.5 rounded-xl bg-[#121214] border border-white/10 text-white hover:border-[#FF8C69]/50 transition-all group"
              aria-label="Abrir Carrinho de Compras"
            >
              <ShoppingBag className="w-5 h-5 text-[#FF8C69] group-hover:scale-105 transition-transform" />
              {cart.item_count > 0 && (
                <span
                  data-testid="cart-badge-count"
                  className="absolute -top-1.5 -right-1.5 bg-[#FF8C69] text-[#0A0A0B] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-lg"
                >
                  {cart.item_count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Auth & SSO Dialog */}
      {showAuthModal && (
        <AuthModal onClose={() => setShowAuthModal(false)} />
      )}
    </>
  );
}
